import type { EnemyType, StackKind, ZoneId } from '../types';

export interface EnemyDef {
  id: EnemyType;
  name: string;
  plural: string;
  /** Stats as functions of zone level L. */
  hp: (L: number) => number;
  attack: (L: number) => number;
  defense: (L: number) => number;
  agi: (L: number) => number;
  per: (L: number) => number;
  dodge: number;
  /** Fraction of HP at which the enemy tries to run (0 = never). */
  retreatAt: number;
  groupMax: number;
  drops: [StackKind, number][];
  phase: 1 | 2 | 3;
  description: string;
}

/**
 * Package 4, part 0 (owner, 7. 10. 2026): what an enemy is called in a zone (the world book). The stats stay by type; a
 * raider is a Tollman or a Foundry raider, a drone one of WARDEN's units. Zones not listed keep the type's own name
 * (a Tollman for a raider: the Tollmen roam; a WARDEN Patrol for a drone).
 */
export interface EnemyShow {
  name: string;
  plural: string;
  description: string;
}

export const ENEMY_SHOW = {
  infected: { name: 'Infected', plural: 'Infected', description: 'Fresh MEND cases. Feverish, fast, drawn to noise and heat.' },
  mutant: { name: 'Mutant', plural: 'Mutants', description: 'Bodies MEND kept mending for two years. Slow, plated in bone.' },
  tollmen: { name: 'Tollman', plural: 'Tollmen', description: 'Road crews in hi-vis vests. They keep a deal while you pay.' },
  foundry: { name: 'Foundry raider', plural: 'Foundry raiders', description: 'Welded plate and soot. They build the guns everyone else uses.' },
  patrol: { name: 'WARDEN Patrol', plural: 'WARDEN Patrols', description: 'Logs violations. Then enforces them.' },
  enforcer: { name: 'WARDEN Enforcer', plural: 'WARDEN Enforcers', description: 'Built to stop residents. It still does.' },
  scanner: { name: 'WARDEN Scanner', plural: 'WARDEN Scanners', description: 'Checks your temperature. Calls the others.' },
} satisfies Record<string, EnemyShow>;

const RAIDERS: Partial<Record<ZoneId, keyof typeof ENEMY_SHOW>> = { factory: 'foundry', military_base: 'foundry', wasteland: 'foundry', survivor_vault: 'foundry' };
const DRONES: Partial<Record<ZoneId, keyof typeof ENEMY_SHOW>> = { military_base: 'enforcer', underground_lab: 'enforcer', survivor_vault: 'enforcer', wasteland: 'scanner' };

export type EnemyShowId = keyof typeof ENEMY_SHOW;

/** Which of the world book's enemies a type is in a zone: a raider's faction, a drone's WARDEN unit (part 0's table). */
export function enemyShowId(type: Exclude<EnemyType, 'survivor' | 'boss'>, zone?: ZoneId | null): EnemyShowId {
  if (type === 'raider') return (zone && RAIDERS[zone]) || 'tollmen';
  if (type === 'drone') return (zone && DRONES[zone]) || 'patrol';
  return type;
}

/** The enemy as the world book names it in a zone (no zone: the type's own, a Tollman or a WARDEN Patrol). */
export function enemyShow(type: Exclude<EnemyType, 'survivor' | 'boss'>, zone?: ZoneId | null): EnemyShow {
  return ENEMY_SHOW[enemyShowId(type, zone)];
}

/** "Tollmen", "2 WARDEN Patrols", "a Mutant" style names for a group in a zone. */
export function enemyGroupName(type: Exclude<EnemyType, 'survivor' | 'boss'>, count: number, zone?: ZoneId | null): string {
  const s = enemyShow(type, zone);
  return count > 1 ? `${count} ${s.plural}` : s.name;
}

export const ENEMIES: Record<Exclude<EnemyType, 'survivor' | 'boss'>, EnemyDef> = {
  infected: {
    id: 'infected', name: 'Infected', plural: 'Infected',
    hp: (L) => 40 + 12 * L, attack: (L) => 6 + 3 * L, defense: (L) => 2 + L, agi: (L) => 8 + 0.8 * L, per: (L) => 8 + 0.5 * L,
    dodge: 0, retreatAt: 0, groupMax: 3,
    drops: [['cloth', 3], ['food', 2], ['scrap', 2]], phase: 1,
    description: 'Numerous, weak, melee.',
  },
  raider: {
    id: 'raider', name: 'Raider', plural: 'Raiders',
    hp: (L) => 55 + 14 * L, attack: (L) => 8 + 3.6 * L, defense: (L) => 4 + 1.5 * L, agi: (L) => 12 + L, per: (L) => 12 + L,
    dodge: 0, retreatAt: 0.2, groupMax: 4,
    drops: [['ammo', 3], ['metal', 2], ['food', 2], ['scrap', 3]], phase: 1,
    description: 'Armed people: the Tollmen on the roads, the Foundry from the Steelworks. They run when the fight turns.',
  },
  mutant: {
    id: 'mutant', name: 'Mutant', plural: 'Mutants',
    hp: (L) => 90 + 22 * L, attack: (L) => 12 + 4.2 * L, defense: (L) => 6 + 2 * L, agi: (L) => 6 + 0.5 * L, per: (L) => 10 + 0.5 * L,
    dodge: 0, retreatAt: 0, groupMax: 1,
    drops: [['chemicals', 3], ['medicine', 1]], phase: 2,
    description: 'Strong melee, very high HP.',
  },
  drone: {
    // Package 4, part 0: WARDEN's units by zone (`enemyShow`); "Rogue Drone" is gone.
    id: 'drone', name: 'WARDEN drone', plural: 'WARDEN drones',
    hp: (L) => 45 + 10 * L, attack: (L) => 9 + 3.4 * L, defense: (L) => 8 + 1.8 * L, agi: (L) => 18 + 1.2 * L, per: (L) => 16 + L,
    dodge: 0.12, retreatAt: 0, groupMax: 2,
    drops: [['electronics', 5]], phase: 3,
    description: 'Fast, hard to hit. Drops Electronics.',
  },
};

/** Group scaling: HP adds up, attack grows slower because they get in each other's way. */
export function groupScale(n: number): { hp: number; attack: number } {
  return { hp: n, attack: 1 + 0.45 * (n - 1) };
}
